import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";

import { useLanguage } from "../../../../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../../../../navigation/DealerRoutes";
import { createPathFromEntries } from "../../../../navigation/createPathFromEntries";
import { getDealerBasePath } from "../../../../navigation/getDealerBasePath";
import { useGetVehicleInfo } from "../../../../queries/useGetVehicleInfo";
import { AnyOrderItem, AnyPartItem } from "../../../../stores/slices/cartSlice";
import { Button } from "../../../../ui/Button/Button";
import { useTabPanels } from "../../../../ui/TabPanels/TabPanels";
import { getProductTypeFromPartType } from "../../../../util/getProductTypeFromPartType";
import { CartButton } from "../../../Cart/CartButton/CartButton";
import { PriceSelect } from "../../../PriceSelect/PriceSelect";
import { PriceSelectEntry } from "../../../PriceSelect/usePriceSelectState";
import { SectionHeading } from "../SectionHeading";

type DealerTireDetailsProps = {
  isStaggered: boolean;
  orderItem: AnyOrderItem | null;
  part: AnyPartItem | null;
  priceEntries: PriceSelectEntry[];
  setPriceEntries(priceEntries: PriceSelectEntry[]): void;
};

export const QuoteForm: FunctionComponent<DealerTireDetailsProps> = ({
  isStaggered,
  orderItem,
  part,
  priceEntries,
  setPriceEntries,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const { data: vehicleInfo } = useGetVehicleInfo();
  const [isVehicleResultsRoute] = useRoute(DealerVehicleResultsRoute);

  const { next } = useTabPanels();

  return (
    <>
      <SectionHeading translation="searchResults.yourPersonalizedQuote" />
      <div
        className={clsx("grid", isStaggered ? "grid-cols-2 gap-3" : "gap-2")}
      >
        <PriceSelect
          disabled={!!orderItem}
          isStaggered={isStaggered}
          itemPriceClassName={clsx(isStaggered && "col-span-2 mb-2")}
          key={part?.partDetails.primaryPart.itemId}
          onChange={setPriceEntries}
          onReady={setPriceEntries}
          orderItem={orderItem}
          part={part || null}
          promotionClassName={clsx(isStaggered && "col-span-2")}
          quantityClassName={clsx(isStaggered && "justify-end ml-auto")}
          quantityKeyClassName={clsx(isStaggered && "hidden")}
          rowClassName="justify-between"
          ruleClassName={clsx(isStaggered && "col-span-2")}
          shouldDisplayRule
          totalClassName={clsx(isStaggered && "col-span-2")}
        />
      </div>
      <div className="grid gap-2 mt-8">
        <CartButton
          className="h-10 md:h-12"
          createPackageClassName="bg-input border border-edge"
          isStaggered={isStaggered}
          part={part}
          pricing={priceEntries}
          primary
          vehicleId={Number(vehicleInfo?.vehicleId) || null}
          vehicleInfo={vehicleInfo || null}
        />
        {isVehicleResultsRoute && (
          <Button
            href={createPathFromEntries(
              [
                [
                  "product",
                  part
                    ? getProductTypeFromPartType(
                        part.partDetails.primaryPart.itemType
                      )
                    : "null",
                ],
                ["year", vehicleInfo?.vehicleYear || "null"],
                ["make", vehicleInfo?.vehicleManufacturerName || "null"],
                ["category", "0"],
                ["model", vehicleInfo?.vehicleModelName?.[language] || "null"],
                [
                  "trim",
                  vehicleInfo?.vehicleSubmodelName?.[language] || "null",
                ],
                ["vehicle", vehicleInfo?.vehicleId || "null"],
                ["product", part?.partDetails.primaryPart.itemId],
                [
                  "secondary",
                  part?.partDetails.secondaryParts[0]?.itemId || "null",
                ],
              ],
              getDealerBasePath()
            )}
            aria-label="openSearchResultsInANewWindow"
            className="bg-input border border-edge"
            disabled={!part}
            element="a"
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("searchResults.openInNewWindow")}
          </Button>
        )}
        <Button
          aria-label="submitDealerForm"
          className="bg-input border border-edge"
          disabled={!part}
          onClick={next}
        >
          {t("searchResults.contactDealer")}
        </Button>
      </div>
    </>
  );
};
